import {
  ApolloClient,
  ApolloProvider as BaseApolloProvider,
  HttpLink,
  from,
  split,
  type NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { isAbortedOrOfflineFetch } from '@orn/shared-web/lib/apollo/isAbortedOrOfflineFetch';
import * as Sentry from '@sentry/react';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import * as React from 'react';

import { IS_DEV, IS_STAGING } from '../env';
import { getApiBaseUrl, getGraphqlWsUrl } from '../lib/getEnv';
import { createUnauthorizedHandler, probeSession } from '../lib/sessionExpiry';
import { isTerminalClose } from '../lib/wsCloseCodes';
import { PATHS } from '../paths';
import { sanitizeVariables } from '../utils/filterUtils';

import { createAppCache, createMutationUpdateHandler } from './apolloCache';

/**
 * How often this client pings the server while a socket is idle, keeping it
 * under the ~60s ALB/CloudFront idle ceiling from the client side. The server
 * runs its own ping on the same cadence, configured separately where
 * `useServer` is called — neither setting controls the other.
 */
const WS_KEEP_ALIVE_MS = 30_000;

/**
 * The graphql-ws client behind the subscriptions link. Module-level and
 * exported so hooks can observe connection lifecycle (`.on('connected', …)`)
 * for refetch-on-reconnect — every disconnect is a loss window, since
 * subscription delivery has no replay. Constructing it opens nothing:
 * `lazy: true` means no socket exists until the first subscription starts,
 * so ungated viewers (their hooks skip subscribing) never connect.
 */

export const subscriptionsWsClient = createClient({
  url: getGraphqlWsUrl,
  lazy: true,
  keepAlive: WS_KEEP_ALIVE_MS,
  shouldRetry: (errOrCloseEvent) => !isTerminalClose(errOrCloseEvent),
});

// Create a single client instance outside the component to avoid recreating on remounts (StrictMode)
let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let isHandlingSessionExpiry = false;

interface IGraphQLErrorLike {
  message: string;
  locations?: unknown;
  extensions?: { code?: unknown };
}

const getGraphQLErrors = (errorResponse: unknown): readonly IGraphQLErrorLike[] | undefined => {
  if (typeof errorResponse !== 'object' || errorResponse === null) {
    return undefined;
  }

  const maybeGraphQLErrors = (errorResponse as { graphQLErrors?: unknown }).graphQLErrors;
  if (!Array.isArray(maybeGraphQLErrors)) {
    return undefined;
  }

  return maybeGraphQLErrors.filter(
    (error): error is IGraphQLErrorLike =>
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
  );
};

const getNetworkError = (errorResponse: unknown): unknown => {
  if (typeof errorResponse !== 'object' || errorResponse === null) {
    return undefined;
  }

  return (errorResponse as { networkError?: unknown }).networkError;
};

const getNetworkStatusCode = (networkError: unknown): number | undefined => {
  if (typeof networkError !== 'object' || networkError === null) {
    return undefined;
  }

  if ('statusCode' in networkError) {
    const statusCode = (networkError as { statusCode?: unknown }).statusCode;
    return typeof statusCode === 'number' ? statusCode : undefined;
  }

  return undefined;
};

const shouldTreatAsUnauthorized = (
  graphQLErrors: readonly { message: string; extensions?: { code?: unknown } }[] | undefined,
  networkError: unknown
): boolean => {
  const hasAuthGraphQLError =
    graphQLErrors?.some((graphQLError) => graphQLError.extensions?.code === 'UNAUTHENTICATED') ?? false;

  if (hasAuthGraphQLError) {
    return true;
  }

  const networkStatusCode = getNetworkStatusCode(networkError);
  if (networkStatusCode === 401) {
    return true;
  }

  // Fallback for environments where statusCode may be missing.
  if (typeof networkError === 'object' && networkError !== null && 'result' in networkError) {
    const result = (networkError as { result?: unknown }).result;
    if (typeof result === 'object' && result !== null && 'error' in result) {
      const resultError = (result as { error?: unknown }).error;
      return typeof resultError === 'string' && resultError.toLowerCase().startsWith('unauthorized');
    }
  }

  return false;
};

const handleSessionExpired = () => {
  if (window.location.pathname === PATHS.LOGIN) {
    return;
  }

  if (isHandlingSessionExpiry) {
    return;
  }
  isHandlingSessionExpiry = true;

  void apolloClient?.clearStore().catch((error) => {
    Sentry.captureException(error, {
      tags: {
        graphql: true,
        auth: true,
      },
      level: 'warning',
    });
  });

  window.location.assign(PATHS.LOGIN);
};

// Probe before ejecting — see createUnauthorizedHandler. Module-scoped so
// concurrent 401s across queries share one probe.
const handleUnauthorizedResponse = createUnauthorizedHandler({
  probe: probeSession,
  isOnLoginRoute: () => window.location.pathname === PATHS.LOGIN,
  onConfirmedExpired: handleSessionExpired,
  onTransientUnauthorized: (operationName, probeResult) => {
    Sentry.captureMessage(`Unauthorized GraphQL response (${operationName}) without confirmed session expiry`, {
      tags: {
        graphql: true,
        auth: true,
        probeResult,
      },
      level: 'warning',
    });
  },
});

function getApolloClient() {
  if (!apolloClient) {
    const cache = createAppCache();

    // Create error link to capture GraphQL errors
    const errorLink = onError((errorResponse) => {
      const graphQLErrors = getGraphQLErrors(errorResponse);
      const networkError = getNetworkError(errorResponse);
      const { operation } = errorResponse;

      if (shouldTreatAsUnauthorized(graphQLErrors, networkError)) {
        handleUnauthorizedResponse(operation.operationName ?? 'Unknown');
        return;
      }

      // Sanitize variables to remove sensitive data before logging
      const sanitizedVariables = sanitizeVariables(operation.variables);
      const variablesString = JSON.stringify(sanitizedVariables, null, 2);
      const queryString = operation.query.loc?.source.body ?? 'Unknown query';
      const operationName = operation.operationName ?? 'Unknown';

      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations }) => {
          Sentry.captureException(new Error(`GraphQL Error (${operationName}): ${message}`), {
            tags: {
              graphql: true,
              operationName: operation.operationName ?? 'Unknown',
            },
            contexts: {
              graphql: {
                operation: operation.operationName ?? 'Unknown',
                query: queryString,
                variables: variablesString,
              },
              error: {
                message,
                locations: JSON.stringify(locations, null, 2),
              },
            },
            level: 'error',
          });
        });
      }

      if (networkError) {
        // Drop fetch failures that originate from the user's side (page
        // unload during navigation, tab suspension, lost connectivity,
        // explicit abort). These have no statusCode and produce huge
        // duplicate clusters whenever the session-expiry redirect aborts
        // multiple in-flight queries at once.
        if (isAbortedOrOfflineFetch(networkError) && getNetworkStatusCode(networkError) === undefined) {
          return;
        }

        let networkErrorMessage = 'Unknown network error';
        if (networkError instanceof Error) {
          networkErrorMessage = networkError.message;
        } else if (typeof networkError === 'string') {
          networkErrorMessage = networkError;
        } else if (typeof networkError === 'number' || typeof networkError === 'boolean') {
          networkErrorMessage = `${networkError}`;
        }

        Sentry.captureException(new Error(`GraphQL Network Error (${operationName}): ${networkErrorMessage}`), {
          tags: {
            graphql: true,
            networkError: true,
            operationName: operation.operationName ?? 'Unknown',
          },
          contexts: {
            graphql: {
              operation: operation.operationName ?? 'Unknown',
              query: queryString,
              variables: variablesString,
            },
            network: {
              statusCode: getNetworkStatusCode(networkError),
            },
          },
          level: 'error',
        });
      }
    });

    const httpLink = new HttpLink({
      uri: `${getApiBaseUrl()}/graphql`,
      credentials: 'include',
    });

    // Subscriptions ride `/ws/graphql`; everything else stays on HTTP.
    const wsLink = new GraphQLWsLink(subscriptionsWsClient);
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION;
      },
      wsLink,
      from([errorLink, httpLink])
    );

    // For temporary cache debugging, see `createCacheInspectorLink` in
    // `src/utils/apollo.ts` and prepend the inspector link here.
    apolloClient = new ApolloClient({
      link: splitLink,
      cache,
      devtools: {
        enabled: IS_DEV || IS_STAGING,
      },
      defaultOptions: {
        mutate: {
          update: createMutationUpdateHandler(),
        },
      },
    });
  }
  return apolloClient;
}

const ApolloProvider = (props: React.PropsWithChildren) => {
  const client = getApolloClient();

  return <BaseApolloProvider client={client}>{props.children}</BaseApolloProvider>;
};

export default ApolloProvider;
